import { BodyText, Col } from '@policygenius/mortar';
import { useMemo } from 'react';
import { Form } from 'react-final-form';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import { POLICY_FEATURES_PATH } from 'routes/paths';

import { BENEFIT_PERIOD_DIAGRAM_ALT, BENEFIT_PERIOD_DIAGRAM_URL, HEADING } from './constants';
import BenefitPeriodForm from './form';
import { BenefitPeriodFormValues } from './form/types';
import { useGetBenefitPeriodQuery } from './graphql/GetBenefitPeriod';
import { useUpdateBenefitPeriodMutation } from './graphql/UpdateBenefitPeriod';
import styles from './styles';
import buildInitialValues from './utils/buildInitialValues';
import { validate } from './utils/validate';

const useStyles = createUseStyles(styles);

const BenefitPeriod = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.BenefitPeriod);
  const classes = useStyles();
  const history = useHistory();

  const { data, loading } = useGetBenefitPeriodQuery();
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const [updateBenefitPeriod] = useUpdateBenefitPeriodMutation({
    onCompleted: () => {
      trackStepCompleted();
      history.push(POLICY_FEATURES_PATH);
    },
  });

  const onSubmit = async (values: BenefitPeriodFormValues) => {
    await updateBenefitPeriod({ variables: { input: values } });
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} title={Steps.BenefitPeriod}>
      <Col push={{ small: 0, medium: 2, large: 3 }} span={{ small: 4, medium: 8, large: 6 }}>
        <BodyText size="large" className={classes.bodyText}>
          The{' '}
          <BodyText as="strong" size="inherit" weight="bold">
            benefit period
          </BodyText>{' '}
          is the length of time the benefit amount will be paid out (each month).
        </BodyText>
        <ul className={classes.list}>
          <BodyText as="li" className={classes.listItem}>
            Policies typically have periods of{' '}
            <BodyText as="strong" size="inherit" weight="bold">
              2, 5 or 10 years or up to retirement age
            </BodyText>{' '}
            (65 or 67).
          </BodyText>
          <BodyText as="li" className={classes.listItem}>
            The longer the period, the higher the cost of the policy.
          </BodyText>
          <BodyText as="li" className={classes.listItem}>
            A 5-year benefit period would cover the average duration of most long-term disabilities.
          </BodyText>
        </ul>
        <img
          className={classes.image}
          src={BENEFIT_PERIOD_DIAGRAM_URL}
          alt={BENEFIT_PERIOD_DIAGRAM_ALT}
        />
      </Col>
      <Form<BenefitPeriodFormValues, InitialValues<BenefitPeriodFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => <BenefitPeriodForm {...formProps} />}
      </Form>
    </NavigatorContent>
  );
};

export default BenefitPeriod;
